import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { 
  Users, 
  Package, 
  Image as ImageIcon, 
  AlertCircle,
  Building,
  TrendingUp
} from "lucide-react";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    users: 0,
    companies: 0,
    models: 0,
    backgrounds: 0,
    failedShoots: 0
  });
  const [companies, setCompanies] = useState([]);
  const [failedShoots, setFailedShoots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [usersRes, companiesRes, modelsRes, bgRes, failedRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/companies`, { withCredentials: true }),
        axios.get(`${API}/models`, { withCredentials: true }),
        axios.get(`${API}/backgrounds`, { withCredentials: true }),
        axios.get(`${API}/admin/shoots/failed`, { withCredentials: true })
      ]);
      
      setStats({
        users: usersRes.data.length,
        companies: companiesRes.data.length,
        models: modelsRes.data.length,
        backgrounds: bgRes.data.length,
        failedShoots: failedRes.data.length
      });
      setCompanies(companiesRes.data.slice(0, 5));
      setFailedShoots(failedRes.data.slice(0, 5));
    } catch (error) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-dashboard" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
            Admin Dashboard
          </h1>
          <p className="text-neutral-600 mt-1">
            Manage platform resources and monitor usage
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-neutral-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{stats.users}</p>
                  <p className="text-xs text-neutral-500">Users</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                  <Building className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{stats.companies}</p>
                  <p className="text-xs text-neutral-500">Companies</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{stats.models}</p>
                  <p className="text-xs text-neutral-500">AI Models</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{stats.backgrounds}</p>
                  <p className="text-xs text-neutral-500">Backgrounds</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-neutral-900">{stats.failedShoots}</p>
                  <p className="text-xs text-neutral-500">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Companies */}
          <Card className="border-neutral-100">
            <CardHeader>
              <CardTitle className="font-display text-lg">Recent Companies</CardTitle>
            </CardHeader>
            <CardContent>
              {companies.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No companies yet</p>
              ) : (
                <div className="space-y-3">
                  {companies.map((company) => (
                    <div key={company.company_id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                      <div>
                        <p className="font-medium text-neutral-900">{company.company_name}</p>
                        <p className="text-sm text-neutral-500">{company.owner?.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-indigo-600">
                          {company.subscription?.skus_used || 0} SKUs
                        </p>
                        <p className="text-xs text-neutral-500 capitalize">{company.industry}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Failed Shoots */}
          <Card className="border-neutral-100">
            <CardHeader>
              <CardTitle className="font-display text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                Failed Generations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {failedShoots.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No failed shoots</p>
              ) : (
                <div className="space-y-3">
                  {failedShoots.map((shoot) => (
                    <div key={shoot.shoot_id} className="p-3 bg-red-50 rounded-lg">
                      <p className="font-medium text-red-900 text-sm">{shoot.shoot_id}</p>
                      <p className="text-xs text-red-600 mt-1">{shoot.error_message || "Unknown error"}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/admin/models">
            <Card className="border-neutral-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">Manage AI Models</p>
                  <p className="text-sm text-neutral-500">Add, edit, or disable AI models</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/admin/backgrounds">
            <Card className="border-neutral-100 hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <p className="font-semibold text-neutral-900">Manage Backgrounds</p>
                  <p className="text-sm text-neutral-500">Add or edit background options</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
